import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { LegalPageLayout } from '../components/LegalPageLayout';
import { SiteHeader } from '../components/SiteHeader';
import { AppFooter } from '../components/AppFooter';
import { useScreenInit } from '../useScreenInit';
export function Privacy() {
  useScreenInit('Privacy');
  const navigate = useNavigate();
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1236">
      <Helmet data-id="element-1237">
        <title data-id="element-1238">Privacy Policy | Affordable Medical Tourism</title>
        <meta name="description" content="Privacy Policy for affordablemedicaltourism.com, detailing how Kosansh Solutions Inc handles your data." data-id="element-1239" />
      </Helmet>

      <SiteHeader brand="Affordable Medical Tourism" links={[{
      label: 'Procedures',
      href: '/procedures'
    }, {
      label: 'How it works',
      href: '/how-it-works'
    }, {
      label: 'Destinations',
      href: '/destinations'
    }, {
      label: 'FAQ',
      href: '/faq'
    }]} contactCtaLabel="Get a free quote" onContactCta={() => navigate('/contact')} data-id="element-1240" />

      <main className="flex-1" data-id="element-1241">
        <LegalPageLayout title="Privacy Policy" lastUpdated="October 15, 2023" illustrative={true} intro={<p className="text-lg text-slate-600 mb-8" data-id="element-1243">
              Kosansh Solutions Inc. ("we," "our," or "us") respects your
              privacy and is committed to protecting your personal data. This
              Privacy Policy outlines how we collect, use, disclose, and
              safeguard your information when you visit
              affordablemedicaltourism.com or interact with our services. Given
              the sensitive nature of medical tourism, we employ stringent data
              protection measures.
            </p>} sections={[{
        id: 'data-collection',
        heading: '1. Information We Collect',
        body: <div className="space-y-4 text-slate-600" data-id="element-1244">
                  <p data-id="element-1245">
                    We collect information that you voluntarily provide to us
                    when you express an interest in obtaining information about
                    us or our products and Services, when you participate in
                    activities on the Website, or otherwise when you contact us.
                  </p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-1246">
                    <li data-id="element-1247">
                      <strong data-id="element-1248">Personal Information:</strong> Name, phone number,
                      and any other contact details provided exclusively through
                      our secure contact forms.
                    </li>
                    <li data-id="element-1249">
                      <strong data-id="element-1250">Health Information:</strong> Details regarding
                      your medical history, desired procedures (e.g., Dental,
                      Cosmetic, Orthopedic), and preferred destinations that you
                      choose to share with our care coordinators to facilitate
                      quotes.
                    </li>
                    <li data-id="element-1251">
                      <strong data-id="element-1252">Automatically Collected Data:</strong> IP
                      addresses, browser characteristics, operating system,
                      language preferences, referring URLs, and information
                      about how and when you use our Website.
                    </li>
                  </ul>
                </div>
      }, {
        id: 'data-usage',
        heading: '2. How We Use Your Information',
        body: <div className="space-y-4 text-slate-600" data-id="element-1253">
                  <p data-id="element-1254">
                    We use personal information collected via our Website for a
                    variety of business purposes described below:
                  </p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-1255">
                    <li data-id="element-1256">
                      <strong data-id="element-1257">To facilitate medical coordination:</strong>{' '}
                      Sharing necessary, anonymized, or consented details with
                      accredited international hospitals to secure accurate
                      treatment quotes.
                    </li>
                    <li data-id="element-1258">
                      <strong data-id="element-1259">To communicate with you:</strong> Responding to
                      your inquiries submitted via our contact forms. We do not
                      use email links on our site to prevent unauthorized
                      scraping and ensure communications are routed through
                      secure channels.
                    </li>
                    <li data-id="element-1260">
                      <strong data-id="element-1261">To improve our Services:</strong> Analyzing usage
                      trends to enhance user experience, optimize our SEO/AEO
                      strategies, and ensure WCAG compliance.
                    </li>
                    <li data-id="element-1262">
                      <strong data-id="element-1263">To enforce our terms and policies:</strong> For
                      business purposes, legal reasons, and contractual
                      obligations.
                    </li>
                  </ul>
                </div>
      }, {
        id: 'data-sharing',
        heading: '3. Information Sharing and Disclosure',
        body: <div className="space-y-4 text-slate-600" data-id="element-1264">
                  <p data-id="element-1265">
                    We only share information with your consent, to comply with
                    laws, to provide you with services, to protect your rights,
                    or to fulfill business obligations.
                  </p>
                  <p data-id="element-1266">Specifically, we may share your data with:</p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-1267">
                    <li data-id="element-1268">
                      <strong data-id="element-1269">Healthcare Providers:</strong> Selected,
                      JCI-accredited (or equivalent) hospitals and clinics
                      abroad, strictly for the purpose of evaluating your case
                      and providing a quote.
                    </li>
                    <li data-id="element-1270">
                      <strong data-id="element-1271">Service Providers:</strong> Third-party vendors
                      who perform services for us or on our behalf and require
                      access to such information to do that work (e.g., secure
                      hosting, data analytics).
                    </li>
                    <li data-id="element-1272">
                      <strong data-id="element-1273">Legal Obligations:</strong> When we are legally
                      required to do so in order to comply with applicable law,
                      governmental requests, a judicial proceeding, court order,
                      or legal process.
                    </li>
                  </ul>
                </div>
      }, {
        id: 'data-security',
        heading: '4. Data Security and Retention',
        body: <div className="space-y-4 text-slate-600" data-id="element-1274">
                  <p data-id="element-1275">
                    We have implemented appropriate technical and organizational
                    security measures designed to protect the security of any
                    personal information we process. All data is persisted
                    securely, and we do not rely on in-memory mock data for
                    production environments.
                  </p>
                  <p data-id="element-1276">
                    We will only keep your personal information for as long as
                    it is necessary for the purposes set out in this privacy
                    notice, unless a longer retention period is required or
                    permitted by law.
                  </p>
                </div>
      }, {
        id: 'contact-privacy',
        heading: '5. Contact Us About Your Privacy',
        body: <div className="space-y-4 text-slate-600" data-id="element-1277">
                  <p data-id="element-1278">
                    If you have questions or comments about this notice, or wish
                    to exercise your data rights (such as access, correction, or
                    deletion), please contact Kosansh Solutions Inc. via our
                    official contact form.
                  </p>
                  <p data-id="element-1279">
                    Kosansh Solutions Inc.
                    <br data-id="element-1280" />
                    Fort Worth, TX
                    <br data-id="element-1281" />
                    Phone: 857-567-2674
                  </p>
                </div>
      }]} data-id="element-1242" />
      </main>

      <AppFooter data-id="element-1282" />
    </div>;
}